import React, { act, useEffect, useMemo, useRef, useState } from "react";
import { useStore } from "../../../stores/store";
import DropdownButton from "./LayersDropdownButton";
import { SecondaryLayer } from "../../../interfaces/layer";

export const LayersTab = () => {
  const config = useStore((state) => state.config);
  const map = useStore((state) => state.map);
  const toggleLayerVisibility = useStore(
    (state) => state.toggleLayerVisibility,
  );
  const getLayerVisibilityById = useStore(
    (state) => state.getLayerVisibilityById,
  );
  const setLayerOpacity = useStore((state) => state.setLayerOpacity);
  const setLayerVisibility = useStore((state) => state.setLayerVisibility);
  const getLayerOpacityById = useStore((state) => state.getLayerOpacityById);
  const getLayerTitleById = useStore((state) => state.getLayerTitleById);
  const isMobile = useStore((state) => state.isMobile);
  const layerVisibility = useStore((state) => state.layerVisibility);

  const allLayers = useMemo(() => {
    if (!config?.primaryLayer || !config?.secondaryLayers) {
      return [];
    }
    const rtnLayers =
      config?.secondaryLayers?.filter((layer) => layer.showInTOC) || [];
    if (config?.primaryLayer?.showInTOC) {
      rtnLayers.unshift({
        ...config.primaryLayer,
        id: config.primaryLayer.id + "-image",
      });
    }
    return rtnLayers as SecondaryLayer[];
  }, [config?.primaryLayer, config?.secondaryLayers]);

  const prevVisibleRef = useRef<string[]>([]);

  const visibleLayerIds = useMemo(() => {
    const ids = Object.entries(layerVisibility)
      .filter(([_, visible]) => visible)
      .map(([id]) => id);
    if (JSON.stringify(prevVisibleRef.current) !== JSON.stringify(ids)) {
      prevVisibleRef.current = ids;
    }
    return prevVisibleRef.current;
  }, [layerVisibility]);

  const [scrollTop, setScrollTop] = useState(0);

  const [opacity, setOpacity] = useState(() => {
    if (visibleLayerIds.length > 0) {
      const first = getLayerOpacityById(visibleLayerIds[0]);
      return first ?? 1; // Default to 1 if no opacity is set
    }
    return 1; // Default to 1 if no active layers
  });

  React.useEffect(() => {
    // Exit quickly if no visibleLayerIds or allLayers
    if (!visibleLayerIds || !allLayers) return;

    const newOpacity = opacity;
    allLayers.forEach(({ id, showInTOC }) => {
      const shouldBeVisible = !!showInTOC && visibleLayerIds.includes(id);
      const isCurrentlyVisible = getLayerVisibilityById(id);
      if (
        isCurrentlyVisible !== undefined &&
        isCurrentlyVisible !== shouldBeVisible
      ) {
        setLayerVisibility(id, shouldBeVisible);
      }

      if (shouldBeVisible) {
        const currentOpacity = getLayerOpacityById(id);
        if (currentOpacity !== newOpacity) {
          setLayerOpacity(id, newOpacity);
        }
      }
    });
  }, [visibleLayerIds, opacity, allLayers]);

  const onLayerToggle = (layer: any) => {
    const isCurrentlyVisible = getLayerVisibilityById(layer.id);

    if (layer.id === config?.primaryLayer.id + "-image" && map?.basemap) {
      // map.basemap.baseLayers.at(-1).visible = !isCurrentlyVisible;
    }

    // Some compatibility checks
    const allowed = layer.allowedWithLayers ?? [];
    const disallowed = layer.notAllowedWithLayers ?? [];
    const isAllowed = allowed.length === 0 || allowed.includes("*");

    const isNotBocked = disallowed.every(
      (id: string) => !visibleLayerIds.includes(id),
    );

    //TODO: If functionality for maintaining compatibility checks is important, will need to uncomment and refactor/simplify so that there is one array with the allowed layers & remove disallowed layers
    // if (!isAllowed || !isNotBocked) {
    //   console.warn(`Layer ${layer.id} cannot be toggled due to compatibility checks.`);
    //   return;
    // }

    // Turn off conflicting laters if necessary
    const turnOffLayers = allLayers?.filter((other) => {
      if (other.id === layer.id) return false;
      const otherLayerVisible = getLayerVisibilityById(other.id);
      if (!otherLayerVisible) return false;

      const otherAllowsThis =
        other.allowedWithLayers?.includes("*") ||
        other.allowedWithLayers?.includes(layer.id);
      const thisAllowsOther =
        layer.allowedWithLayers?.includes("*") ||
        layer.allowedWithLayers?.includes(other.id);

      const isAllowedBothWays = otherAllowsThis && thisAllowsOther;
      const isBlocked = layer.notAllowedWithLayers?.includes(other.id) ?? false;
      return !isAllowedBothWays || isBlocked;
    });

    turnOffLayers?.forEach((layerToTurnOff) => {
      toggleLayerVisibility(layerToTurnOff.id);
    });

    // Toggle the layer visibility
    toggleLayerVisibility(layer.id);

    // Now apply the opacity if being turned on
    if (!isCurrentlyVisible) {
      setLayerOpacity(layer.id, opacity);
    }
  };

  return (
    <div className="md:max-h-[55vh] px-2 mt-2 flex flex-col">
      <div
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        className="grow overflow-y-auto md:max-w-52"
      >
        {allLayers
          ?.filter(({ showInTOC }) => showInTOC)
          .map((layer, i) => (
            <div key={i} className="flex items-center">
              {layer.groupLayers && layer.groupLayers.length ? (
                isMobile ? (
                  <></>
                ) : (
                  <DropdownButton
                    groupLayer={layer}
                    activeIds={visibleLayerIds}
                    scrollTop={scrollTop}
                  />
                )
              ) : isMobile ? (
                <label
                  className="fieldset-label flex items-center my-1"
                  onClick={() => {
                    onLayerToggle(layer);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={visibleLayerIds.includes(layer.id)}
                    className="toggle mr-2"
                  />
                  {layer.title}
                </label>
              ) : (
                <button
                  onClick={() => {
                    onLayerToggle(layer);
                  }}
                  className={`py-0.5 px-2 w-full md:w-48 rounded-sm my-1 ${
                    visibleLayerIds.includes(layer.id) ? "active font-bold" : ""
                  } ${layer.buttonColor ? "" : "btn-athena"}`}
                  style={{
                    backgroundColor: layer.buttonColor ? layer.buttonColor : "",
                    color: layer.buttonFontColor ? layer.buttonFontColor : "",
                    boxShadow: visibleLayerIds.includes(layer.id)
                      ? "inset 0 0 10px rgba(0,0,0,.55), 0 2px 4px rgba(0,0,0,.25)"
                      : "",
                  }}
                >
                  {layer.title}
                </button>
              )}
            </div>
          ))}
      </div>
      <div className="my-1 flex-1">
        <p className="text-black md:text-white">Transparency:</p>
        <p className="text-black md:text-white text-xs italic">
          (
          {visibleLayerIds.length === 1
            ? getLayerTitleById(visibleLayerIds[0])
            : visibleLayerIds.length > 1
              ? `${visibleLayerIds.length - 2} layers selected`
              : "No layer selected"}
          )
        </p>
        <div className="flex items-center w-full justify-between">
          <input
            type="range"
            min="0"
            max="100"
            step="1"
            value={visibleLayerIds.length > 0 || "" ? (1 - opacity) * 100 : 0}
            className="slider grow w-full m-auto"
            onChange={(e) => {
              const newOpacity = 100 - +e.currentTarget.value;
              setOpacity(newOpacity / 100);
              visibleLayerIds.forEach((id) =>
                setLayerOpacity(id, newOpacity / 100),
              );
            }}
          />
          <span className="text-black md:text-white ml-1 flex-1 text-right min-w-10">
            {visibleLayerIds.length > 0
              ? `${Math.round((1 - opacity) * 100)} %`
              : ""}
          </span>
        </div>
      </div>
    </div>
  );
};
